import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { api } from '../lib/api';
import { getCurrentCompanyId } from '../lib/company';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent } from '../components/ui/card';
import { ShieldCheck, Filter, Banknote, Receipt, User as UserIcon, Building2, CreditCard } from 'lucide-react';
import DocumentActions from '../components/ui/document-actions';
import { toast } from 'sonner';

export default function Audit() {
  const { user } = useAuth();
  const isMaster = ['master', 'superadmin'].includes(user?.role_id);
  const [data, setData] = useState(null);
  const [companies, setCompanies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({ company_id: getCurrentCompanyId() || '', from_date: '', to_date: '' });

  useEffect(() => {
    init();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const init = async () => {
    try {
      const compsRes = await api.getCompanies(true);
      setCompanies(compsRes.data || []);
      await load();
    } catch (e) {
      toast.error('SalesPRO: Error al cargar auditoría');
    } finally {
      setLoading(false);
    }
  };

  const load = async () => {
    const params = {};
    if (filters.company_id) params.company_id = filters.company_id;
    if (filters.from_date) params.from_date = filters.from_date;
    if (filters.to_date) params.to_date = filters.to_date;
    const res = await api.getReceivedPayments(params);
    setData(res.data);
  };

  const applyFilters = async () => {
    try { await load(); } catch (e) { toast.error('SalesPRO: Error al filtrar'); }
  };

  const filterByCompany = async (companyName) => {
    const comp = companies.find(c => c.name === companyName);
    if (!comp) return;
    const next = { ...filters, company_id: comp.company_id };
    setFilters(next);
    try {
      const params = {};
      if (next.company_id) params.company_id = next.company_id;
      if (next.from_date) params.from_date = next.from_date;
      if (next.to_date) params.to_date = next.to_date;
      const res = await api.getReceivedPayments(params);
      setData(res.data);
    } catch (e) { toast.error('SalesPRO: Error al filtrar'); }
  };

  if (loading) return <div className="flex items-center justify-center h-full">Cargando SalesPRO...</div>;

  const payments = data?.payments || [];
  const byCompany = data?.by_company || {};
  const byMethod = data?.by_method || {};

  return (
    <div className="space-y-4 sm:space-y-6" data-testid="audit-page">
      <div>
        <h1 className="font-heading text-2xl sm:text-3xl font-bold text-[#022C22] flex items-center gap-2">
          <ShieldCheck size={26} /> Auditoría Financiera — Pagos Recibidos
        </h1>
        <p className="text-sm text-[#64748B] mt-1">
          {filters.company_id ? 'Vista por empresa' : 'Consolidado global de todas las empresas activas'} · quién recibió, cuándo y cómo
        </p>
      </div>

      {/* Filtros */}
      <Card className="border border-[#E2E8F0]">
        <CardContent className="p-3 sm:p-4">
          <div className="flex flex-col sm:flex-row sm:items-end gap-3">
            {isMaster && (
              <div className="flex-1">
                <Label className="text-xs">Empresa</Label>
                <select
                  value={filters.company_id}
                  onChange={(e) => setFilters({ ...filters, company_id: e.target.value })}
                  className="w-full px-3 py-2 border border-[#E2E8F0] rounded-md text-sm bg-white"
                  data-testid="audit-company-filter"
                >
                  <option value="">Global (todas las activas)</option>
                  {companies.map(c => <option key={c.company_id} value={c.company_id}>{c.name}</option>)}
                </select>
              </div>
            )}
            <div className="flex-1">
              <Label className="text-xs">Desde</Label>
              <Input type="date" value={filters.from_date} onChange={(e) => setFilters({ ...filters, from_date: e.target.value })} data-testid="audit-from-date" />
            </div>
            <div className="flex-1">
              <Label className="text-xs">Hasta</Label>
              <Input type="date" value={filters.to_date} onChange={(e) => setFilters({ ...filters, to_date: e.target.value })} data-testid="audit-to-date" />
            </div>
            <Button onClick={applyFilters} data-testid="audit-apply-filters">
              <Filter size={16} className="mr-1.5" /> Filtrar
            </Button>
          </div>
          <div className="mt-3 pt-3 border-t border-[#E2E8F0]">
            <p className="text-xs text-[#64748B] mb-1.5 font-medium uppercase tracking-wide">Exportar / compartir auditoría (PDF de marca)</p>
            <DocumentActions
              fetchBlob={() => api.getBrandedReportPdf({
                type: 'audit',
                company_id: filters.company_id || undefined,
                from_date: filters.from_date || undefined,
                to_date: filters.to_date || undefined,
              })}
              filename={`SalesPRO_auditoria_${(filters.from_date || 'hist')}.pdf`}
              title={`SalesPRO — Auditoría de Pagos · Total recibido $${(data?.total_received || 0).toFixed(2)} · ${data?.count || 0} pagos`}
            />
          </div>
        </CardContent>
      </Card>

      {/* KPIs */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <Card className="border border-[#E2E8F0] bg-[#022C22] text-white">
          <CardContent className="p-4 flex items-center justify-between">
            <div className="flex items-center gap-2 text-sm uppercase tracking-wide text-white/70">
              <Banknote size={16} /> Total recibido ({data?.count || 0} pagos)
            </div>
            <p className="text-2xl font-bold" data-testid="audit-total">${(data?.total_received || 0).toFixed(2)}</p>
          </CardContent>
        </Card>
        <Card className="border border-[#E2E8F0]">
          <CardContent className="p-4">
            <p className="text-xs uppercase tracking-wide text-[#94A3B8] mb-2 flex items-center gap-1"><CreditCard size={13} /> Por método de pago</p>
            <div className="flex flex-wrap gap-2">
              {Object.keys(byMethod).length === 0 ? <span className="text-sm text-[#94A3B8]">Sin datos</span> :
                Object.entries(byMethod).map(([m, amt]) => (
                  <span key={m} className="px-2 py-1 bg-[#F1F5F9] rounded text-xs">{m}: <b>${amt.toFixed(2)}</b></span>
                ))}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Consolidado por empresa (global) */}
      {!filters.company_id && Object.keys(byCompany).length > 0 && (
        <Card className="border border-[#E2E8F0]">
          <CardContent className="p-4">
            <p className="text-xs uppercase tracking-wide text-[#94A3B8] mb-2 flex items-center gap-1"><Building2 size={13} /> Recibido por empresa</p>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
              {Object.entries(byCompany).map(([comp, amt]) => (
                <button key={comp} onClick={() => filterByCompany(comp)}
                  className="flex items-center justify-between p-2 bg-[#F8FAFC] rounded hover:bg-[#ECFDF5] hover:ring-1 hover:ring-primary transition-colors text-left"
                  data-testid={`audit-company-cta-${comp}`}>
                  <span className="text-sm text-[#475569] truncate">{comp}</span>
                  <span className="font-semibold text-green-700">${amt.toFixed(2)}</span>
                </button>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Lista de pagos */}
      {payments.length === 0 ? (
        <div className="text-center py-12 text-[#64748B]" data-testid="audit-empty">No hay pagos recibidos en el período seleccionado</div>
      ) : (
        <div className="space-y-2">
          {payments.map((p) => (
            <Card key={p.payment_id || p.created_at} className="border border-[#E2E8F0]" data-testid="audit-payment-row">
              <CardContent className="p-3 flex flex-col sm:flex-row sm:items-center justify-between gap-2">
                <div className="min-w-0">
                  <p className="font-semibold text-sm text-[#022C22] flex items-center gap-1.5 truncate">
                    <Receipt size={14} className="text-primary shrink-0" />
                    {p.client_name} {p.invoice_number ? `· ${p.invoice_number}` : ''}
                  </p>
                  <p className="text-xs text-[#64748B] truncate">
                    {p.company_name} · {(p.created_at || '').slice(0, 16).replace('T', ' ')}
                    {p.method_name ? ` · ${p.method_name}` : ''}{p.reference ? ` · Ref: ${p.reference}` : ''}
                  </p>
                  <p className="text-xs text-[#94A3B8] flex items-center gap-1 mt-0.5">
                    <UserIcon size={11} /> Recibido por: {p.received_by_name}
                  </p>
                </div>
                <span className="font-bold text-green-700 text-lg shrink-0">${Number(p.amount).toFixed(2)}</span>
              </CardContent>
              {p.payment_id && (
                <div className="px-3 pb-3">
                  <DocumentActions
                    fetchBlob={() => api.getPaymentReceipt(p.payment_id)}
                    filename={`comprobante_${(p.payment_id || '').slice(0, 8)}.pdf`}
                    title={`SalesPRO — Comprobante de pago · ${p.client_name} · $${Number(p.amount).toFixed(2)}`}
                    size="sm"
                    compact
                  />
                </div>
              )}
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
